import { useEffect, useMemo, useState } from "react";
import { useMutation } from "@apollo/client";
import { useForm } from "react-hook-form";
import { Navigate } from "react-router-dom";

import Button from "@/components/Button";
import { CardSkeleton } from "@/components/Skeleton";
import { showErrorSnackbar, showSnackbar } from "@/lib/snackbarUtils";
import { UPSERT_CAREER_INTENT_MUTATION } from "../mutations";
import CareerGoalsForm from "./_CareerGoalsForm";
import CareerGoalsTab from "./_CareerGoalsTab";
import CareerProfileFocusedChrome from "./_CareerProfileFocusedChrome";
import { useCareerProfileQuery } from "./_useCareerProfileQuery";
import {
  buildUpsertCareerIntentInput,
  defaultCareerIntentFormValues,
  getCareerIntentSteps,
  hydrateCareerIntentForm,
  validateCareerIntentStep,
  type CareerIntentFormValues
} from "./careerIntentForm";

export default function CareerProfileGoalsPage() {
  const {
    pgaId,
    flagsLoaded,
    capabilitiesLoaded,
    employmentRedirect,
    loading,
    error,
    currentGoal,
    memberClass,
    canManageCareerPlan,
    refetch
  } = useCareerProfileQuery();
  const canEdit = Boolean(canManageCareerPlan);
  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [stepError, setStepError] = useState<string>();
  const goals = currentGoal ? [currentGoal] : [];

  const form = useForm<CareerIntentFormValues>({
    defaultValues: defaultCareerIntentFormValues()
  });
  const formValues = form.watch();
  const steps = useMemo(
    () => getCareerIntentSteps({ selectedIntents: formValues.selectedIntents }),
    [formValues.selectedIntents]
  );
  const currentStep = steps[Math.min(stepIndex, Math.max(steps.length - 1, 0))];
  const isFirstStep = stepIndex === 0;
  const isLastStep = Boolean(currentStep) && stepIndex === steps.length - 1;
  const showProgress = formValues.selectedIntents.length > 0;

  useEffect(() => {
    if (stepIndex >= steps.length) {
      setStepIndex(Math.max(steps.length - 1, 0));
    }
  }, [stepIndex, steps.length]);

  const [upsertCareerIntent] = useMutation(UPSERT_CAREER_INTENT_MUTATION);

  const startEditing = () => {
    form.reset(hydrateCareerIntentForm(currentGoal));
    setStepIndex(0);
    setStepError(undefined);
    setIsEditing(true);
  };

  const cancelEditing = () => {
    form.reset(hydrateCareerIntentForm(currentGoal));
    setStepIndex(0);
    setStepError(undefined);
    setIsEditing(false);
  };

  const saveGoals = form.handleSubmit(async (values) => {
    if (!pgaId) return;

    setIsSubmitting(true);
    try {
      const { data: mutationData, errors } = await upsertCareerIntent({
        variables: { input: buildUpsertCareerIntentInput(pgaId, values) }
      });

      if (errors?.length) {
        showErrorSnackbar(errors[0]?.message || "Failed to save Career Goals");
        return;
      }

      const payload = mutationData?.upsertCareerIntent;
      if (!payload?.success) {
        showErrorSnackbar(payload?.message || "Failed to save Career Goals");
        return;
      }

      showSnackbar("Career Goals saved");
      setIsEditing(false);
      setStepIndex(0);
      setStepError(undefined);
      void refetch();
    } catch (saveError) {
      showErrorSnackbar(saveError instanceof Error ? saveError.message : "Failed to save Career Goals");
    } finally {
      setIsSubmitting(false);
    }
  });

  const goPrevious = () => {
    if (isSubmitting || stepIndex === 0) return;
    setStepError(undefined);
    setStepIndex((index) => index - 1);
  };

  const goContinue = () => {
    if (isSubmitting || !currentStep) return;

    const validationMessage = validateCareerIntentStep(form.getValues(), currentStep.id);
    if (validationMessage) {
      setStepError(validationMessage);
      return;
    }

    setStepError(undefined);
    if (!isLastStep) {
      setStepIndex((index) => index + 1);
      return;
    }

    void saveGoals();
  };

  if (!flagsLoaded || !capabilitiesLoaded) return null;
  if (employmentRedirect) return <Navigate to={employmentRedirect} replace />;
  if (!pgaId) return null;

  const showCreate = canEdit && !isEditing && goals.length === 0;
  const showEdit = canEdit && !isEditing && goals.length > 0;

  const actions = isEditing ? (
    <>
      <Button variant="outlined" disabled={isSubmitting} onClick={cancelEditing} testId="career-goals-cancel">
        Cancel
      </Button>
      {!isFirstStep ? (
        <Button variant="outlined" disabled={isSubmitting} onClick={goPrevious} testId="career-goals-previous">
          Previous
        </Button>
      ) : null}
      <Button
        disabled={isSubmitting}
        icon={isSubmitting ? "progress_activity" : undefined}
        onClick={goContinue}
        testId={isLastStep ? "career-goals-save" : "career-goals-continue"}
      >
        {isLastStep ? "Save Plan" : "Continue"}
      </Button>
    </>
  ) : showCreate ? (
    <Button onClick={startEditing} testId="career-goals-create">
      Create Career Goals
    </Button>
  ) : showEdit ? (
    <Button variant="outlined" onClick={startEditing} testId="career-goals-edit">
      Edit
    </Button>
  ) : null;

  return (
    <CareerProfileFocusedChrome pgaId={pgaId} actions={actions} activeTab="goals" hideTabs={isEditing}>
      {loading ? (
        <CardSkeleton className="mt-12 w-full" rows={6} />
      ) : error ? (
        <div className="mt-12">Error: {error.message}</div>
      ) : isEditing && currentStep ? (
        <CareerGoalsForm
          currentRole={memberClass}
          disabled={isSubmitting}
          errorMessage={stepError}
          form={form}
          showProgress={showProgress}
          step={currentStep.id}
          stepIndex={stepIndex}
          stepTitle={currentStep.title}
          stepsLength={steps.length}
        />
      ) : (
        <CareerGoalsTab goals={goals} isLatest onCreate={showCreate ? startEditing : undefined} />
      )}
    </CareerProfileFocusedChrome>
  );
}
